import {
  accountSearchColumns,
  chungTuSearchColumns,
  hinhThucThanhToanSearchColumns,
  QUERY_KEYS,
  quyenChungTuSearchColumns
} from '@/constants';
import type { DataTableRenderCellParams as TableCellParams } from '@/components/custom/erp/data-table-v2';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import { AccountModel, ChungTu, HinhThucThanhToan, QuyenChungTu } from '@/types/schemas';
import { FormMode } from '@/types/form';

export const getAccountInfoTableColumns = (
  formMode: FormMode,
  bankAccounts: BanHangRecord[],
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void
) => [
  {
    field: 'ma_httt',
    headerName: 'Mã hình thức',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <SearchField<HinhThucThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HINH_THUC_THANH_TOAN}/`}
        searchColumns={hinhThucThanhToanSearchColumns}
        columnDisplay='ma_httt'
        dialogTitle='Danh mục hình thức thanh toán'
        value={params.row.ma_httt_data?.ma_httt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_httt_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_httt',
      resultField: 'ma_httt_data',
      detailEndpoint: QUERY_KEYS.HINH_THUC_THANH_TOAN
    }
  },
  {
    field: 'ten_httt',
    headerName: 'Tên hình thức',
    width: 200,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='ten_vt'
        type='text'
        value={params.row.ma_httt_data?.ten_httt || ''}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'tknh',
    headerName: 'Tài khoản ngân hàng',
    width: 100,
    renderCell: (params: TableCellParams) => {
      const bankAccountOptions = bankAccounts.map(item => ({
        value: item.uuid,
        label: `[${item.ma_ngan_hang_data?.ten_ngan_hang}] - ${item.tknh}`
      }));

      return (
        <CellField
          name='tknh'
          type='select'
          value={params.row.tknh}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tknh', newValue)}
          options={bankAccountOptions}
          disabled={formMode === 'view'}
        />
      );
    }
  },
  {
    field: 'tk',
    headerName: 'Tài khoản thanh toán',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.ma_httt_data?.tk_data?.code || params.row.tk_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk',
      resultField: 'tk_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ma_ct',
    headerName: 'Chứng từ',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <SearchField<ChungTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHUNG_TU}/`}
        searchColumns={chungTuSearchColumns}
        columnDisplay='ma_ct'
        dialogTitle='Danh mục chứng từ'
        value={params.row.ma_httt_data?.ma_ct_data?.ma_ct || params.row.ma_ct_data?.ma_ct || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ct_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ct',
      resultField: 'ma_ct_data',
      detailEndpoint: QUERY_KEYS.CHUNG_TU
    }
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày chứng từ',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='ngay_ct'
        type='date'
        value={params.row.ngay_ct || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_nk',
    headerName: 'Quyển chứng từ',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <SearchField<QuyenChungTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.QUYEN_CHUNG_TU}/`}
        searchColumns={quyenChungTuSearchColumns}
        columnDisplay='ma_nk'
        dialogTitle='Danh mục quyển chứng từ'
        value={params.row.ma_httt_data?.ma_nk_data?.ma_nk || params.row.ma_nk_data?.ma_nk || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_nk_data', row)}
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_nk',
      resultField: 'ma_nk_data',
      detailEndpoint: QUERY_KEYS.QUYEN_CHUNG_TU
    }
  },
  {
    field: 't_tt_nt',
    headerName: 'Số tiền thanh toán',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='t_tt_nt'
        type='number'
        value={params.row.t_tt_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_tt_nt', newValue)}
        disabled={formMode === 'view'}
        commitOnBlur
      />
    )
  }
];
